import {createContext, useContext, useMemo, type ReactNode} from "react";

/**
 * Minimal viewer identity shared with search result cards so they can resolve
 * per-viewer follow/star state (client-side Firestore subscriptions) and route
 * anonymous follow/star clicks to sign-up.
 *
 * `uid`, `email`, and `handle` are `null` for anonymous viewers. The flag
 * evaluator uses these values for user and segment targeting. `authResolved`
 * becomes true after Firebase auth reports its initial state.
 */
export interface Viewer {
    readonly uid: string | null;
    readonly email: string | null;
    readonly handle: string | null;
    readonly authResolved: boolean;
}

const ViewerContext = createContext<Viewer>({uid: null, email: null, handle: null, authResolved: false});

export function ViewerProvider(props: {
    uid: string | null;
    email: string | null;
    handle: string | null;
    authResolved: boolean;
    children: ReactNode;
}) {
    const {uid, email, handle, authResolved, children} = props;
    const value = useMemo<Viewer>(() => ({uid, email, handle, authResolved}), [uid, email, handle, authResolved]);
    return <ViewerContext.Provider value={value}>{children}</ViewerContext.Provider>;
}

export function useViewer(): Viewer {
    return useContext(ViewerContext);
}
